import { GTMEvent } from '../types/gtm-types';

declare global {
  interface Window {
    dataLayer: Record<string, any>[];
  }
}

/**
 * Sends a structured event to Google Tag Manager (GTM).
 *
 * This function ensures that events follow the correct GTM format and
 * prevents TypeScript errors by enforcing a strict event structure.
 *
 * @template T - The type of the GTM event, ensuring valid event properties.
 * @param {T} eventData - The event object containing event name and relevant data.
 *
 * @example
 * // Track a page view event
 * sendGTMEvent({
 *   event: 'page_view',
 *   page_location: window.location.href,
 *   page_path: window.location.pathname,
 *   page_title: document.title,
 * });
 *
 * @example
 * // Track an add to cart event
 * sendGTMEvent({
 *   event: 'add_to_cart',
 *   currency: 'USD',
 *   value: 29.99,
 *   items: [
 *     { id: 'product_2', name: 'Ebook', category: 'Digital', quantity: 1, price: 29.99 },
 *   ],
 * });
 */
export const sendGTMEvent = <T extends GTMEvent>(eventData: T) => {
  if (typeof window !== 'undefined') {
    // Ensure dataLayer exists before pushing events
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(eventData);
  }
};
